import React, { FC, useEffect, useState } from 'react';
import { Dropdown, Input, Table, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image } from 'common/components';
import { useDebounce, useSearchParamsHook } from 'common/hooks';
import { ERoute, ESearchParams } from 'common/const';
import { onInputKeyDown } from 'common/helpers';
import { CHARACTER_LIMIT_MIN } from 'common/config';
import { Catalog, Close, Search } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  generateCatalogSearchValueHighlightedText,
  renderCatalogSearchDropdownProductsRecords,
} from 'entities/Catalog/Catalog.helper';
import { getSearchSuggestions, clearSearch, selectSearch } from 'entities/Search/Search.slice';
import { ICatalog } from 'entities/Catalog/Catalog.models';

interface IComponentProps {
  catalog?: ICatalog;
}

export const CatalogSearch: FC<IComponentProps> = ({ catalog }) => {
  const dispatch = useAppDispatch();
  // search
  const search = useAppSelector(selectSearch);

  const [searchValue, setSearchValue] = useState<string>('');
  const [focused, setFocused] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getSearchParam } = useSearchParamsHook();

  const defaultValue = getSearchParam(ESearchParams.Search);

  const toggleFocused = () => setFocused((prev) => !prev);

  const onDropdownOpenChange = (open: boolean) => {
    if (!open) {
      dispatch(clearSearch());
    }
  };

  const handleCategoryClick = (categoryId: number) => {
    if (!catalog) {
      return;
    }

    dispatch(clearSearch());
    navigate(`${ERoute.Catalog}/${catalog.id}/${categoryId}`, { state: location.state });
  };

  const onSearchClick = () => {
    if (!catalog) {
      return;
    }

    dispatch(clearSearch());
    navigate(`${ERoute.Catalog}/${catalog.id}?${ESearchParams.Search}=${searchValue}`, { state: location.state });
  };

  useEffect(() => {
    if (defaultValue) {
      setSearchValue(defaultValue);
    } else {
      setSearchValue('');
    }
  }, [catalog, defaultValue]);

  useDebounce(() => {
    if (catalog && focused && searchValue.length >= CHARACTER_LIMIT_MIN) {
      dispatch(getSearchSuggestions({ catalogId: catalog.id, search: searchValue }));
    } else {
      dispatch(clearSearch());
    }
  }, [catalog, searchValue, focused]);

  return (
    <div className="flex-1 flex align-center gap-4">
      <Dropdown
        overlayClassName="redesign"
        overlayStyle={{ width: 'min-content' }}
        open={!!search.categories.length || !!search.goods.length}
        onOpenChange={onDropdownOpenChange}
        trigger={['click']}
        disabled={!catalog}
        dropdownRender={() => {
          return (
            <div className="custom-dropdown flex align-start">
              <div className="mr-6 flex-1" style={{ maxWidth: 480 }}>
                <div className="mb-6 ff-medium fs-xs color-bright-green">Категории</div>

                <div className="catalog-search__dropdown-categories-scroll scrollbar">
                  {search.categories.map((item) => {
                    return (
                      <div
                        key={item.id}
                        className="relative mb-4 cursor-pointer overflow-hidden catalog-search__dropdown-categories-item"
                        onClick={() => handleCategoryClick(item.id)}
                      >
                        <div className="relative flex align-center catalog-search__dropdown-categories-item-container">
                          <div className="mr-7">
                            <Image size={40} src={item.image} />
                          </div>

                          <span style={{ flex: 1 }}>
                            {generateCatalogSearchValueHighlightedText(item.displayName, searchValue)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1">
                <span className="mb-6 ff-medium fs-xs color-bright-green">Товары</span>

                <Table
                  showHeader={false}
                  pagination={false}
                  size="small"
                  className="table-hover-bg catalog-search__dropdown-products-table"
                  dataSource={search.goods.map((goods) => ({ ...goods, key: goods.id }))}
                  columns={renderCatalogSearchDropdownProductsRecords(searchValue)}
                />
              </div>
            </div>
          );
        }}
      >
        <div className="catalog-search__container">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Искать по названию или артикулу товара"
            disabled={!catalog}
            onFocus={toggleFocused}
            onBlur={toggleFocused}
            onKeyDown={(e) => onInputKeyDown(e, onSearchClick)}
          />

          {!!searchValue.length && (
            <Button
              className="catalog-search__clear-btn"
              icon={<Close className="icon-close-input" />}
              onClick={() => setSearchValue('')}
            />
          )}

          {catalog && (
            <div className="catalog-search__catalog">
              <Catalog className="icon-catalog-bright-green" />

              <span className="text-controls-accent color-bright-green">{catalog.name}</span>
            </div>
          )}
        </div>
      </Dropdown>

      <Button
        className="button-circle primary"
        icon={<Search className="icon-search-white" />}
        onClick={onSearchClick}
        disabled={!catalog}
      />
    </div>
  );
};
